import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/api';
import { useAuth } from '../utils/auth';

const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedUser, setSelectedUser] = useState(null); // For modal
    const [modalActionLoading, setModalActionLoading] = useState(false);

    // Modal Form State
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [isLifetime, setIsLifetime] = useState(false);

    const navigate = useNavigate();
    const { logout } = useAuth();

    useEffect(() => {
        fetchData();
    }, [navigate]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersRes, plansRes] = await Promise.all([
                authFetch('/admin/users'),
                authFetch('/admin/plans')
            ]);

            if (!usersRes.ok || !plansRes.ok) throw new Error('Failed to fetch data');

            const usersData = await usersRes.json();
            const plansData = await plansRes.json();

            setUsers(usersData);
            setPlans(plansData);
        } catch (err) {
            console.error(err);
            setError('Failed to load dashboard data.');
            if (err.message && err.message.includes('Unauthorized')) {
                navigate('/admin');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/admin');
    };

    const openManageModal = (user) => {
        setSelectedUser(user);
        // Pre-select current plan if any
        const currentPlanName = user.subscription?.planName;
        const currentPlan = plans.find(p => p.name === currentPlanName);
        if (currentPlan) {
            setSelectedPlanId(currentPlan.id);
        } else if (plans.length > 0) {
            setSelectedPlanId(plans[0].id); // Default to first plan
        }
        setIsLifetime(user.subscription?.isLifetime || false);
    };

    const closeManageModal = () => {
        setSelectedUser(null);
        setModalActionLoading(false);
    };

    /* Actions */
    const handleUpdatePlan = async () => {
        if (!selectedUser || !selectedPlanId) return;
        setModalActionLoading(true);
        try {
            const res = await authFetch(`/admin/users/${selectedUser.id}/subscription`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    planId: selectedPlanId,
                    isLifetime: isLifetime
                })
            });
            if (!res.ok) throw new Error('Failed to update plan');
            alert('Plan updated successfully!');
            fetchData(); // Refresh list
            closeManageModal();
        } catch (err) {
            console.error(err);
            alert('Error updating plan');
        } finally {
            setModalActionLoading(false);
        }
    };

    const handleBlockUser = async () => {
        if (!selectedUser) return;
        if (!window.confirm(`Are you sure you want to ${selectedUser.isBlocked ? 'UNBLOCK' : 'BLOCK'} this user?`)) return;

        setModalActionLoading(true);
        try {
            const res = await authFetch(`/admin/users/${selectedUser.id}/block`, { method: 'PUT' });
            if (!res.ok) throw new Error('Failed to toggle block');
            fetchData();
            closeManageModal();
        } catch (err) {
            console.error(err);
            alert('Error changing block status');
        } finally {
            setModalActionLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        if (!window.confirm('DANGER: Are you sure you want to DELETE this user? This cannot be undone.')) return;

        setModalActionLoading(true);
        try {
            const res = await authFetch(`/admin/users/${selectedUser.id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete user');
            fetchData();
            closeManageModal();
        } catch (err) {
            console.error(err);
            alert('Error deleting user');
        } finally {
            setModalActionLoading(false);
        }
    };

    if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading dashboard...</div>;

    return (
        <div style={{ padding: '2rem', backgroundColor: '#f9f9f9', minHeight: '100vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1>Admin Dashboard</h1>
                <button onClick={handleLogout} style={{ padding: '0.5rem 1rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}>Logout</button>
            </div>

            {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}

            <div style={{ overflowX: 'auto', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #eee', background: '#f1f1f1' }}>
                            <th style={{ padding: '1rem' }}>ID</th>
                            <th style={{ padding: '1rem' }}>Name</th>
                            <th style={{ padding: '1rem' }}>Email</th>
                            <th style={{ padding: '1rem' }}>Role/Status</th>
                            <th style={{ padding: '1rem' }}>Plan</th>
                            <th style={{ padding: '1rem' }}>Expiry</th>
                            <th style={{ padding: '1rem' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.id} style={{ borderBottom: '1px solid #eee', opacity: user.isBlocked ? 0.6 : 1 }}>
                                <td style={{ padding: '1rem' }}>{user.id}</td>
                                <td style={{ padding: '1rem' }}>{user.name || '-'}</td>
                                <td style={{ padding: '1rem' }}>{user.email}</td>
                                <td style={{ padding: '1rem' }}>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <span style={{
                                            background: user.role === 'admin' ? '#d1e7dd' : '#cfe2ff',
                                            color: user.role === 'admin' ? '#0f5132' : '#084298',
                                            padding: '0.25rem 0.5rem',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem'
                                        }}>
                                            {user.role}
                                        </span>
                                        {user.isBlocked && (
                                            <span style={{ background: '#f8d7da', color: '#842029', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>
                                                BLOCKED
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td style={{ padding: '1rem' }}>{user.subscription?.planName || 'None'}</td>
                                <td style={{ padding: '1rem' }}>
                                    {
                                        user.role === 'admin' ?
                                            <span style={{ color: '#198754', fontWeight: 'bold' }}>Lifetime ♾️ (Admin)</span> :
                                            (
                                                user.subscription?.isLifetime ||
                                                    (user.subscription?.endDate && new Date(user.subscription.endDate).getFullYear() > 2030) ||
                                                    (user.subscription?.planName && user.subscription.planName.toLowerCase().includes('lifetime'))
                                                    ? <span style={{ color: '#198754', fontWeight: 'bold' }}>Lifetime ♾️</span>
                                                    : (user.subscription?.endDate ? new Date(user.subscription.endDate).toLocaleDateString() : '-')
                                            )
                                    }
                                </td>
                                <td style={{ padding: '1rem' }}>
                                    <button
                                        onClick={() => openManageModal(user)}
                                        disabled={user.role === 'admin'}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: '#0d6efd',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: user.role === 'admin' ? 'not-allowed' : 'pointer',
                                            opacity: user.role === 'admin' ? 0.5 : 1
                                        }}
                                    >
                                        Manage
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* MANAGE MODAL */}
            {selectedUser && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white', padding: '2rem', borderRadius: '8px', width: '90%', maxWidth: '500px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        <h2 style={{ marginBottom: '1.5rem' }}>Manage User: {selectedUser.email}</h2>

                        {/* Plan Section */}
                        <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #eee' }}>
                            <h3>Update Subscription</h3>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Select Plan</label>
                                <select
                                    value={selectedPlanId}
                                    onChange={(e) => {
                                        const newPlanId = e.target.value;
                                        setSelectedPlanId(newPlanId);
                                        const plan = plans.find(p => p.id === Number(newPlanId));
                                        if (plan && plan.name.toLowerCase().includes('lifetime')) {
                                            setIsLifetime(true);
                                        } else {
                                            setIsLifetime(false);
                                        }
                                    }}
                                    style={{ width: '100%', padding: '0.5rem' }}
                                >
                                    {plans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name} - {
                                                Number(plan.price) === 0 ? '7 Days' :
                                                    plan.name.toLowerCase().includes('lifetime') ? `₹${plan.price} / One Time` :
                                                        `₹${plan.price}/${plan.billing_cycle}`
                                            }
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={isLifetime}
                                        onChange={(e) => setIsLifetime(e.target.checked)}
                                    />
                                    Grant Lifetime Access (Overrides duration)
                                </label>
                            </div>
                            <button
                                onClick={handleUpdatePlan}
                                disabled={modalActionLoading}
                                style={{ width: '100%', padding: '0.75rem', background: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                {modalActionLoading ? 'Saving...' : 'Save Plan Changes'}
                            </button>
                        </div>

                        {/* Danger Zone */}
                        <div>
                            <h3 style={{ color: '#dc3545', marginBottom: '1rem' }}>User Actions</h3>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={handleBlockUser}
                                    disabled={modalActionLoading}
                                    style={{ flex: 1, padding: '0.75rem', background: selectedUser.isBlocked ? '#6c757d' : '#ffac00', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                    {selectedUser.isBlocked ? 'Unblock User' : 'Block User'}
                                </button>
                                <button
                                    onClick={handleDeleteUser}
                                    disabled={modalActionLoading}
                                    style={{ flex: 1, padding: '0.75rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                    Delete User
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={closeManageModal}
                            style={{ marginTop: '2rem', width: '100%', padding: '0.5rem', background: 'transparent', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
